import React, { useMemo, useState } from "react";
import { Money } from "../../../components";
import { Icon, IconButton } from "../../../components/ui";
import RemovePriceConfirm from "./RemovePriceConfirm";
import axios from "axios";
import PriceItem from "./PriceItem";
import { monthLabels, boxLabels } from "../../../product/constants";
import { sourceOptions } from "../../../product/constants";
import CollapseButton from "./CollapseButton";
import { conditionLabels } from "../../../product/constants";
import useTradeImage from "./useTradeImage";

const ucFirst = (string) => {
  return string ? string[0].toUpperCase() + string.substring(1) : string;
};

const convertString = (string) => {
  let arr;
  try {
    arr = JSON.parse(string);
  } catch (_) {
    return undefined; // missing/blank/non-JSON — render nothing
  }
  if (arr && arr.length > 0) {
    return arr.map((item) => conditionLabels[item] || item).join(", ");
  }
};

const PriceItemRenderer = ({
  item,
  onItemRemoved,
  setModalItem,
  setModalOpen,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const tradeImage = useTradeImage(item);
  const year = item?.year_month?.substr(0, 4);
  const month = item?.year_month?.substr(5, 2);
  const monthLabel = monthLabels?.[month * 1];
  const locationName = item?.location?.replace("-e", " E").replace("uk", "UK");

  const getSourceName = (source) => {
    return sourceOptions.find((option) => option.id === source)?.name;
  };

  const removeItem = async (id) => {
    try {
      await axios.delete(`/api/product-prices/${id}/delete`);
      onItemRemoved();
    } catch (error) {
      // Reported to the console rather than swallowed. The confirm modal is
      // driven by `isRemoving`, so closing it moved to `finally`: a failed
      // delete used to leave it open with no message and no way to tell.
      console.error("Failed to remove price", error);
    } finally {
      setIsRemoving(false);
    }
  };

  const sortedRecords = useMemo(() => {
    if (!item?.additional_records?.length) return [];
    return [...item.additional_records].sort(
      (a, b) => new Date(b.price_date) - new Date(a.price_date)
    );
  }, [item?.additional_records]);

  const latestRecord = useMemo(() => {
    if (!item?.additional_records?.length) return item;

    const allRecords = [item, ...item.additional_records];
    return allRecords.reduce((latest, current) => {
      return new Date(current.price_date) > new Date(latest.price_date)
        ? current
        : latest;
    });
  }, [item]);

  const price = useMemo(() => {
    if (item?.additional_records?.length > 0) {
      if (isOpen) {
        return parseInt(item?.gbp_price / 100) * 100;
      } else {
        return parseInt(latestRecord?.gbp_price / 100) * 100;
      }
    }

    return parseInt(item?.gbp_price / 100) * 100;
  }, [item, isOpen, latestRecord]);

  const collapsedUnderprice = useMemo(() => {
    return (
      !isOpen &&
      (item?.additional_records?.some((record) => record.under_priced) ?? false)
    );
  }, [item, isOpen]);

  // The link used to be href="javascript::void(0)" — the double colon is a
  // JS syntax error, so every click threw "SyntaxError: Unexpected token ':'"
  // at the page URL. It is a real <button> now (IconButton), so nothing navigates.
  const handleOpen = (e) => {
    e?.preventDefault?.();
    setModalItem({
      product_id: item.product_id,
      message_id: item.listing_code,
      messageStatus: "approved",
    });
    setModalOpen(true);
  };

  return (
    <>
      <tr
        key={item.id}
        className="price-list"
        style={{
          backgroundColor: `${
            item.under_priced ? "#A4E6A0" : collapsedUnderprice ? "#A4E6A0" : ""
          }`,
        }}
      >
        <td style={{ width: 20 }}>
          {item?.additional_records?.length > 0 && (
            <CollapseButton isOpen={isOpen} setIsOpen={setIsOpen} />
          )}
        </td>
        <td className="price-list__thumb">
          {tradeImage.src ? (
            <img src={tradeImage.src} alt="" loading="lazy" />
          ) : tradeImage.showPlaceholder ? (
            <span className="price-list__no-photo">No photo</span>
          ) : null}
        </td>
        <td>
          {(isOpen ? item?.price_date : latestRecord?.price_date)?.substr(
            0,
            10
          )}
          &nbsp;
        </td>
        <td>{getSourceName(item?.source)}&nbsp;</td>
        <td>
          {monthLabel ? `${monthLabel} ` : ""}
          {year}&nbsp;
        </td>
        <td>{convertString(item?.condition)}&nbsp;</td>
        <td>
          <IconButton
            variant="ghost"
            size="sm"
            label="Open price source"
            data-btn="price-open"
            onClick={handleOpen}
          >
            <Icon name="ExternalLink" size="sm" />
          </IconButton>
        </td>
        <td>
          {/* price is the gbp_price pivot — explicit GBP. */}
          <Money amount={price} currency="gbp" style={{ fontSize: "14px" }} />
        </td>
        <td>{boxLabels?.[item?.box]}&nbsp;</td>
        <td>
          {item.supplier_id ? item.supplier_name : item.supplier}
          &nbsp;
        </td>
        <td>{item.store_name}&nbsp;</td>
        <td>{ucFirst(locationName)}&nbsp;</td>
        <td>
          {isOpen === false && item?.additional_records > 0 ? (
            <>&nbsp;</>
          ) : (
            <IconButton
              variant="ghost"
              size="sm"
              label="Remove price"
              data-btn="price-delete"
              onClick={() => setIsRemoving(true)}
            >
              <Icon name="Trash2" size="sm" />
            </IconButton>
          )}
        </td>
      </tr>

      {isOpen &&
        sortedRecords.map((record, index) => (
          <PriceItem
            item={record}
            key={index}
            removeItem={removeItem}
            isRemoving={isRemoving}
            setIsRemoving={setIsRemoving}
          />
        ))}

      <RemovePriceConfirm
        isOpen={isRemoving}
        onCancel={() => setIsRemoving(false)}
        onRemove={() => removeItem(item.id)}
      />
    </>
  );
};

export default PriceItemRenderer;
